/*
 * Purpose: Mission-scoped knowledge — docs, AI request forms, vault fields (all missions)
 * Last modified: 2026-06-28 | Agent
 * Completeness: 98
 */

export const MISSION_REQUEST_FIELD_TYPES = [
  'text',
  'textarea',
  'number',
  'email',
  'url',
  'date',
  'phone',
  'select',
  'multiselect',
  'boolean',
  'tags',
  'rating',
  'file_text',
  'vault_secret',
  'info',
] as const;

export type MissionRequestFieldType = (typeof MISSION_REQUEST_FIELD_TYPES)[number];

export type MissionFieldStorage = 'mission' | 'vault';

export type MissionKnowledgeField = {
  id: string;
  label: string;
  type: MissionRequestFieldType;
  placeholder?: string;
  helpText?: string;
  required?: boolean;
  options?: string[];
  storage?: MissionFieldStorage;
  vaultSecretName?: string;
  maxRating?: number;
  body?: string;
};

export type MissionKnowledgeRequest = {
  id: string;
  gapRef: string;
  title: string;
  description?: string;
  layout?: 'form' | 'callout';
  fields: MissionKnowledgeField[];
};

/** Any mission document — title, body, optional freeform category label. */
export type MissionKnowledgeDoc = {
  id: string;
  missionSessionId: string;
  title: string;
  body: string;
  /** Optional user/AI label — e.g. rubric, template, export (not enforced). */
  category?: string;
  gapRefs: string[];
  createdAt: number;
};

import type { MissionAutomationConsent } from '@/lib/missionAutomationPolicy';
import type { MissionRiskApproval } from '@/lib/missionRiskPolicy';

export type MissionKnowledgeSessionData = {
  docs: MissionKnowledgeDoc[];
  structuredAnswers: Record<string, string>;
  /** Explicit opt-in for mission-scoped auto-connect / auto-deploy (default: all off). */
  automationConsent?: MissionAutomationConsent;
  /** Human approval for risky items before use (keyed by missionRiskPolicy item id). */
  riskApprovals?: Record<string, MissionRiskApproval>;
};

export type MissionKnowledgeSummary = {
  missionSessionId: string;
  docCount: number;
  contextBlock: string;
};

export const VAULT_FIELD_MARKER = '__vault_ok__:';
